import React, { FC, useState } from 'react';
import Image from 'next/image';
import { EntryFields } from 'contentful';

import { FeatureFlag } from '@/core/featureFlags';
import { TypeProduct, TypeTabNavigation } from '@/core/generated-types';
import useFeature from '@/hooks/useFeature';
import { ProductOptionsRadio } from '@/components/common/ProductOptionsAddToCart';
import {
  InventoryStatusCode,
  ShopifyProduct,
  ShopifyVariant,
} from '@/lib/shopify';
import { ProductVariantCopy } from '@/lib/contentful';
import { Button } from '@/components';
import AddButton from '@/components/common/InventoryStatus/AddButton';
import { PlayKitDetailsBodyTreatment } from './PlayKitDetailsHeaderTreatment';
import { SelectedAddOns } from '@/components/common/ProductAddToCart/ProductAddToCart';
import { EnhancedTypeTabContent } from '@/components/common/TabNavigation/TabNavigation';
import { ProductAddOns } from '@/components/common/ProductAddOns';
import { LanguageSelector } from '@/components/subflow-v3/components/LanguageSelector';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';
import { getVariantTranslation } from '@/utils/getVariantTranslation';
import AddToCartErrorMessage from '@/components/common/AddToCartErrorMessage/AddToCartErrorMessage';
import {
  trackItemClickedEvent,
  CTA_NAMES,
  CLICKED_ITEM_TYPES,
  PAGE_LOCATIONS,
} from '@/utils/rudderstack';
import { isEnglishOnlyPlayKit } from '@/utils/productHelpers';
interface CTAButtonProps {
  product: TypeProduct;
  productSelected: TypeProduct;
  isStandaloneSelected: boolean;
  inventoryData: any[] | undefined;
  isLoading: boolean;
  handleClick: (productSelected?: TypeProduct) => void;
  onError: (error: string | null) => void;
}

const CTAButton: FC<CTAButtonProps> = ({
  product,
  productSelected,
  isStandaloneSelected,
  inventoryData,
  isLoading,
  handleClick,
  onError,
}) => {
  if (!productSelected.fields.cta) return null;

  if (isStandaloneSelected && inventoryData?.[0]?.isBelowThreshold) {
    return (
      <AddButton
        inventoryStatus={InventoryStatusCode.OUT_OF_STOCK}
        product={product}
        isLoading={isLoading}
        isDisabledFromCaller={false}
        dataTestId="out-of-stock-button"
      />
    );
  }

  return (
    <Button
      type="submit"
      ctaText={productSelected.fields.cta?.fields.linkText}
      classes="lg:mt-4 mx-0 h-14 lg:h-16"
      onClick={() => {
        trackItemClickedEvent({
          type: CLICKED_ITEM_TYPES.BUTTON,
          cta: CTA_NAMES.GET_STARTED,
          pageLocation: PAGE_LOCATIONS.PLAY_KITS_PDP,
          url: window.location.href,
        });

        try {
          handleClick(productSelected);
        } catch (error) {
          onError("This item wasn't added to your cart. Please try again.");
        }
      }}
      testId="main-cta-button"
      fullWidth
      experimentSelectors={{
        context: 'playkit-pdp',
        element: 'primary-cta',
      }}
    />
  );
};

interface CompletePlayKitTabContentProps {
  isStandaloneEnabled: boolean;
  subscriptionProduct: TypeProduct;
  standaloneProduct: TypeProduct | undefined;
  subscriptionShopifyProduct: ShopifyProduct;
  standaloneShopifyProduct: ShopifyProduct | undefined;
  productSelected: TypeProduct;
  handleOptionSelected: (
    product: TypeProduct,
    shopifyProduct: ShopifyProduct,
    variant: ShopifyVariant,
  ) => void;
  product: TypeProduct;
  isStandaloneSelected: boolean;
  inventoryData: any[] | undefined;
  isLoading: boolean;
  handleClick: (productSelected?: TypeProduct) => void;
  shipMessage?: string;
  cta?: any;
  productVariantsCopy: ProductVariantCopy | null;
}

const CompletePlayKitTabContent: FC<CompletePlayKitTabContentProps> = ({
  isStandaloneEnabled,
  subscriptionProduct,
  standaloneProduct,
  subscriptionShopifyProduct,
  standaloneShopifyProduct,
  productSelected,
  handleOptionSelected,
  product,
  isStandaloneSelected,
  inventoryData,
  isLoading,
  handleClick,
  shipMessage,
  cta,
  productVariantsCopy,
}) => {
  const [ctaError, setCTAError] = useState<string | null>(null);
  const isEUStore = process.env.NEXT_PUBLIC_REGION === 'EU';
  const { data, api } = useSubFlowV3Data();
  const translations = product.fields.translations?.fields;
  const optionDetails = product.fields.optionDetails;
  const evergreenMessaging = useFeature(
    FeatureFlag.EVERGREEN_DISCOUNT_MESSAGING,
    'off',
  );
  const hideCancelAnyTime =
    String(evergreenMessaging).toLowerCase() === 'show-messaging';

  if (isStandaloneEnabled && standaloneProduct && standaloneShopifyProduct) {
    return (
      <div className="pt-6">
        <ProductOptionsRadio
          products={[subscriptionProduct, standaloneProduct]}
          productSelected={productSelected}
          shopifyProducts={[
            subscriptionShopifyProduct,
            standaloneShopifyProduct,
          ]}
          onChange={handleOptionSelected}
          originalProduct={product}
        />
        {isEUStore && (
          <>
            <p className="text-sm font-bold">
              {getVariantTranslation(productVariantsCopy || null, 'Language')}
            </p>
            <LanguageSelector
              data={data}
              isPdpLangSelector
              productVariantsCopy={productVariantsCopy}
              onSelect={(newLanguage) => {
                api.updateLanguage(newLanguage);
                //  Reset addons selection
                api.toggleSubscriptionAddOns([]);
              }}
            />
          </>
        )}
        <CTAButton
          product={product}
          productSelected={productSelected}
          isStandaloneSelected={isStandaloneSelected}
          inventoryData={inventoryData}
          isLoading={isLoading}
          handleClick={handleClick}
          onError={setCTAError}
        />
        {ctaError && <AddToCartErrorMessage error={ctaError} />}
      </div>
    );
  }

  return (
    <div className="pt-4">
      <PlayKitDetailsBodyTreatment
        aboutHeading={'The Play Kits include'}
        shipMessage={shipMessage}
        optionDetails={optionDetails}
      />
      {cta && (
        <Button
          type="submit"
          ctaText={cta.fields.linkText}
          classes="mt-6 lg:mt-4 mx-0 h-14 lg:h-16"
          onClick={() => {
            try {
              setCTAError(null);
              handleClick();
            } catch (error) {
              setCTAError(
                "This item wasn't added to your cart. Please try again.",
              );
            }
          }}
          fullWidth
        />
      )}

      {ctaError && <p className="text-sm text-warning mt-4">{ctaError}</p>}
      {isStandaloneEnabled ? (
        translations?.ctaMessage && (
          <div className="flex gap-2 items-start justify-center mt-4">
            <p className="text-sm md:text-base">{translations.ctaMessage}</p>
          </div>
        )
      ) : hideCancelAnyTime ? null : (
        <div className="flex gap-2 items-start justify-center mt-4">
          <Image
            src="https://images.ctfassets.net/0sea1vycfyqy/6RW4n2JlYnbOqYHtD8Yupr/d68b2b60faf61022512b826bb586bb42/Delivered_Box.png"
            alt="Delivered box"
            width={21}
            height={24}
            className="mx-0"
          />
          <p className="text-sm md:text-base">
            {/* This is hardcoded for the A/B test */}
            Cancel any time.
          </p>
        </div>
      )}
    </div>
  );
};

interface CreateTabsProps {
  isStandaloneEnabled: boolean;
  subscriptionProduct: TypeProduct;
  standaloneProduct: TypeProduct | undefined;
  subscriptionShopifyProduct: ShopifyProduct;
  standaloneShopifyProduct: ShopifyProduct | undefined;
  productSelected: TypeProduct;
  handleOptionSelected: (
    product: TypeProduct,
    shopifyProduct: ShopifyProduct,
    variant: ShopifyVariant,
  ) => void;
  product: TypeProduct;
  isStandaloneSelected: boolean;
  inventoryData: any[] | undefined;
  isLoading: boolean;
  handleClick: (productSelected?: TypeProduct) => void;
  shipMessage?: string;
  cta?: any;
  tabsComponent?: TypeTabNavigation;
  selectedAddOns: SelectedAddOns[];
  setSelectedAddOns: (selectedAddOns: SelectedAddOns[]) => void;
  defaultVariant: ShopifyVariant;
  bottomMarkerRef: React.RefObject<HTMLDivElement>;
  handleAddToCart: () => void;
  subscribeCtaMessage?: EntryFields.RichText;
  productVariantsCopy: ProductVariantCopy | null;
}

export const createTabs = ({
  isStandaloneEnabled,
  subscriptionProduct,
  standaloneProduct,
  subscriptionShopifyProduct,
  standaloneShopifyProduct,
  productSelected,
  handleOptionSelected,
  product,
  isStandaloneSelected,
  inventoryData,
  isLoading,
  handleClick,
  shipMessage,
  cta,
  tabsComponent,
  selectedAddOns,
  setSelectedAddOns,
  defaultVariant,
  bottomMarkerRef,
  handleAddToCart,
  subscribeCtaMessage,
  productVariantsCopy,
}: CreateTabsProps): EnhancedTypeTabContent[] => [
  {
    ...tabsComponent?.fields.tabs[0],
    fields: {
      ...tabsComponent?.fields.tabs[0].fields,
      customComponent: (
        <CompletePlayKitTabContent
          isStandaloneEnabled={isStandaloneEnabled}
          subscriptionProduct={subscriptionProduct}
          standaloneProduct={standaloneProduct}
          subscriptionShopifyProduct={subscriptionShopifyProduct}
          standaloneShopifyProduct={standaloneShopifyProduct}
          productSelected={productSelected}
          handleOptionSelected={handleOptionSelected}
          product={product}
          isStandaloneSelected={isStandaloneSelected}
          inventoryData={inventoryData}
          isLoading={isLoading}
          handleClick={handleClick}
          shipMessage={shipMessage}
          cta={cta}
          productVariantsCopy={productVariantsCopy}
        />
      ),
    },
  } as EnhancedTypeTabContent,
  {
    ...tabsComponent?.fields.tabs[1],
    fields: {
      ...tabsComponent?.fields.tabs[1].fields,
      customComponent: (
        <div className="text-base md:text-lg">
          <div className="text-base md:text-lg">
            <ProductAddOns
              productAddOns={productSelected.fields.productAddOns}
              selectedAddOns={selectedAddOns}
              setSelectedAddOns={setSelectedAddOns}
              productFields={productSelected.fields}
              defaultVariant={defaultVariant}
              isPlaythingProduct
              bottomMarkerRef={bottomMarkerRef}
              handleAddToCart={handleAddToCart}
              subscribeCtaMessage={subscribeCtaMessage}
            />
          </div>
        </div>
      ),
    },
  } as EnhancedTypeTabContent,
];

interface NoTabsContentProps {
  isStandaloneEnabled: boolean;
  isResaleValuePdpEnabled: boolean;
  subscriptionProduct: TypeProduct;
  standaloneProduct: TypeProduct | undefined;
  subscriptionShopifyProduct: ShopifyProduct;
  standaloneShopifyProduct: ShopifyProduct | undefined;
  productSelected: TypeProduct;
  handleOptionSelected: (
    product: TypeProduct,
    shopifyProduct: ShopifyProduct,
    variant: ShopifyVariant,
  ) => void;
  product: TypeProduct;
  isStandaloneSelected: boolean;
  inventoryData: any[] | undefined;
  isLoading: boolean;
  handleClick: (productSelected?: TypeProduct) => void;
  shipMessage?: string;
  cta?: any;
  productVariantsCopy: ProductVariantCopy | null;
  isBfcmEnabled?: boolean;
}

export const NoTabsContent: FC<NoTabsContentProps> = ({
  isStandaloneEnabled,
  isResaleValuePdpEnabled,
  subscriptionProduct,
  standaloneProduct,
  subscriptionShopifyProduct,
  standaloneShopifyProduct,
  productSelected,
  handleOptionSelected,
  product,
  isStandaloneSelected,
  inventoryData,
  isLoading,
  handleClick,
  shipMessage,
  cta,
  productVariantsCopy,
  isBfcmEnabled = false,
}) => {
  const [ctaError, setCTAError] = useState<string | null>(null);
  const isEUStore = process.env.NEXT_PUBLIC_REGION === 'EU';
  const { data, api } = useSubFlowV3Data();
  const translations = product.fields.translations?.fields;
  const isEnglishOnly = isEnglishOnlyPlayKit(
    product.fields?.shopifyProduct?.handle,
    isEUStore,
  );
  const evergreenMessagingNoTabs = useFeature(
    FeatureFlag.EVERGREEN_DISCOUNT_MESSAGING,
    'off',
  );
  const hideCancelAnyTimeNoTabs =
    String(evergreenMessagingNoTabs).toLowerCase() === 'show-messaging';

  if (isStandaloneEnabled && standaloneProduct && standaloneShopifyProduct) {
    return (
      <>
        <ProductOptionsRadio
          products={[subscriptionProduct, standaloneProduct]}
          productSelected={productSelected}
          shopifyProducts={[
            subscriptionShopifyProduct,
            standaloneShopifyProduct,
          ]}
          isBfcmEnabled={isBfcmEnabled}
          onChange={handleOptionSelected}
          originalProduct={product}
        />
        {isEUStore && (
          <>
            <p className="text-sm font-bold">
              {getVariantTranslation(productVariantsCopy || null, 'Language')}
            </p>
            {isEnglishOnly ? (
              <button
                className="w-full border border-gray-400 mt-2 mb-1 px-4 py-3 text-left relative rounded-sm bg-white"
                disabled
              >
                {getVariantTranslation(
                  productVariantsCopy || null,
                  'English Only',
                )}
              </button>
            ) : (
              <LanguageSelector
                data={data}
                isPdpLangSelector
                productVariantsCopy={productVariantsCopy}
                onSelect={(newLanguage) => {
                  api.updateLanguage(newLanguage);
                  //  Reset addons selection
                  api.toggleSubscriptionAddOns([]);
                }}
              />
            )}
            <p className="text-sm">
              {getVariantTranslation(productVariantsCopy || null, 'Caption')}
            </p>
          </>
        )}
        <CTAButton
          product={product}
          productSelected={productSelected}
          isStandaloneSelected={isStandaloneSelected}
          inventoryData={inventoryData}
          isLoading={isLoading}
          handleClick={handleClick}
          onError={setCTAError}
        />

        {ctaError && <AddToCartErrorMessage error={ctaError} />}

        {isResaleValuePdpEnabled && translations?.ctaMessage && (
          <p
            data-testid="resale-value-cta-message"
            className="whitespace-pre-wrap mt-4 text-center text-base max-w-[300px] mx-auto md:text-lg md:max-w-[320px]"
          >
            {translations.ctaMessage}
          </p>
        )}
      </>
    );
  }

  return (
    <>
      <div className="border-b-[1px] border-charcoal-opacity-20 mt-1 mb-4 lg:mt-7 lg:mb-6" />
      <PlayKitDetailsBodyTreatment
        aboutHeading={'The Play Kits include'}
        shipMessage={shipMessage}
        optionDetails={product.fields.optionDetails}
      />
      {isEUStore && (
        <>
          <p className="text-sm font-bold">
            {getVariantTranslation(productVariantsCopy || null, 'Language')}
          </p>
          <LanguageSelector
            data={data}
            isPdpLangSelector
            productVariantsCopy={productVariantsCopy}
            onSelect={(newLanguage) => {
              api.updateLanguage(newLanguage);
              //  Reset addons selection
              api.toggleSubscriptionAddOns([]);
            }}
          />
        </>
      )}
      {cta && (
        <Button
          type="submit"
          ctaText={cta.fields.linkText}
          classes="mt-6 lg:mt-4 mx-0 h-14 lg:h-16"
          onClick={() => {
            try {
              setCTAError(null);
              handleClick();
            } catch (error) {
              setCTAError(
                "This item wasn't added to your cart. Please try again.",
              );
            }
          }}
          fullWidth
        />
      )}

      {ctaError && <AddToCartErrorMessage error={ctaError} />}
      {isStandaloneEnabled ? (
        translations?.ctaMessage && (
          <div className="flex gap-2 items-start justify-center mt-4">
            <p className="text-sm md:text-base">{translations.ctaMessage}</p>
          </div>
        )
      ) : hideCancelAnyTimeNoTabs ? null : (
        <div className="flex gap-2 items-start justify-center mt-4">
          <Image
            src="https://images.ctfassets.net/0sea1vycfyqy/6RW4n2JlYnbOqYHtD8Yupr/d68b2b60faf61022512b826bb586bb42/Delivered_Box.png"
            alt="Delivered box"
            width={21}
            height={24}
            className="mx-0"
          />
          <p className="text-sm md:text-base">
            {/* This is hardcoded for the A/B test */}
            Cancel any time.
          </p>
        </div>
      )}
    </>
  );
};
